import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { nameof } from "../helpers/nameof";
import { AreaEntityNode, EntityPermissionNode, RoleAccessSettings, SpecialActionNode, EntityAccesses } from "../models/roles";
import { AuthService } from "./auth.service";
import { SingletonCheckHelper } from '../helpers/singleton-check.helper';
import { permissionAreaKeyNameOf } from '../helpers/permission.helpers';
import { LookupsPermissionHelper } from '../actions/admin/helpers/lookups-permission-helper';

@Injectable({
    providedIn: 'root'
})
export class PermissionsService {

    public get permissions$(): Observable<RoleAccessSettings> {
        return this.authService.currentUser$.pipe(map(x => {
            return x ? x.permissions : null
        }));
    }

    public get currentPermissions(): RoleAccessSettings {
        const currentUser = this.authService.currentUserValue;
        return currentUser ? currentUser.permissions : null;
    }

    constructor(private authService: AuthService) {
        SingletonCheckHelper.ensureSingleton(this);
    }

    public getEntityPermissions$(areaKey: string, entityKey: string): Observable<EntityPermissionNode> {
        return this.authService.currentUser$.pipe(map(x => {
            const perm = x ? x.permissions : null;

            if (perm == null)
                return null;

            const entityNode = this.getEntityNode(perm, areaKey, entityKey);

            if (!entityNode)
                return null;

            return entityNode;
        }));
    }

    public checkAreaAccess(areaKey: string): boolean {
        const currentPermissions = this.currentPermissions;

        if (!currentPermissions)
            return false;

        const areaNode: AreaEntityNode = currentPermissions.entities[areaKey];

        if (!areaNode)
            return false;            

        let hasAccessToArea = Object.keys(areaNode)
            .filter(x => x !== nameof<AreaEntityNode>("title"))
            .map(x => areaNode[x] as EntityPermissionNode)
            .some(x => x.canRead === true);

        if(!hasAccessToArea && permissionAreaKeyNameOf("admin") === areaKey) {
            hasAccessToArea = LookupsPermissionHelper.hasUserAccessToAnyLookup(currentPermissions);
        }

        return hasAccessToArea;
    }

    public checkEntityAccess<T extends EntityAccesses, K1 extends keyof T, K2 extends keyof T[K1]>(area: K1, entity: K2): boolean  {
        const areaKey = area as string;
        const entityKey = entity as string;
        
        const currentPermissions = this.currentPermissions;

        if (!currentPermissions)
            return false;

        const entityNode = this.getEntityNode(currentPermissions, areaKey, entityKey);

        return entityNode && entityNode.canRead === true;
    }

    public checkSpecialActionAccess(specialActionKey: string) : boolean{
        const currentPermissions = this.currentPermissions;

        if (!currentPermissions)
            return false;

        const specialAction: SpecialActionNode = (this.currentPermissions.specialActions as any)[specialActionKey];

        return specialAction && specialAction.canExecute === true;
    }

    private getEntityNode(accessSettings: RoleAccessSettings, areaKey: string, entityKey: string): EntityPermissionNode {
        const areaNode: AreaEntityNode = accessSettings.entities[areaKey];

        if (!areaNode)
            return null;

        const entityNode: EntityPermissionNode = areaNode[entityKey];

        return entityNode;
    }
}
